/**
 * Common dynamic Business Insights - works for any change history data.
 * No hard-coded categories. Colorful cards.
 */
import * as React from 'react';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import type { ChangeHistoryRow } from './ChangeHistoryTimelineModal';

interface InsightCard {
  label: string;
  value: string | number;
  subtext?: string;
  color: string;
  bgColor: string;
}

function computeInsights(rows: ChangeHistoryRow[]): InsightCard[] {
  const cards: InsightCard[] = [];
  if (!Array.isArray(rows) || rows.length === 0) return cards;

  const byField = new Map<string, number>();
  const byUser = new Map<string, number>();
  const now = Date.now();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  let last7Days = 0;

  rows.forEach(r => {
    const fc = String(r.fieldChanged ?? '—').trim() || '—';
    byField.set(fc, (byField.get(fc) ?? 0) + 1);
    const user = String(r.userName ?? '—').trim() || '—';
    byUser.set(user, (byUser.get(user) ?? 0) + 1);
    const t = r.created ? new Date(String(r.created)).getTime() : 0;
    if (now - t <= sevenDaysMs) last7Days++;
  });

  cards.push({
    label: 'Total changes',
    value: rows.length,
    color: '#0D47A1',
    bgColor: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
  });

  if (last7Days > 0) {
    cards.push({
      label: 'Recent activity',
      value: last7Days,
      subtext: 'in last 7 days',
      color: '#1B5E20',
      bgColor: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
    });
  }

  const fieldEntries = Array.from(byField.entries()).sort((a, b) => b[1] - a[1]);
  if (fieldEntries.length > 0) {
    cards.push({
      label: 'Most changed',
      value: fieldEntries[0][0],
      subtext: `${fieldEntries[0][1]} change${fieldEntries[0][1] === 1 ? '' : 's'}`,
      color: '#E65100',
      bgColor: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
    });
  }

  const userEntries = Array.from(byUser.entries()).sort((a, b) => b[1] - a[1]);
  if (userEntries.length > 0) {
    const top = userEntries[0];
    const pct = rows.length > 0 ? Math.round((top[1] / rows.length) * 100) : 0;
    cards.push({
      label: 'Top contributor',
      value: top[0],
      subtext: `${top[1]} (${pct}%)`,
      color: '#6A1B9A',
      bgColor: 'linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)',
    });
  }

  if (fieldEntries.length > 1) {
    const uniqueFields = fieldEntries.length;
    cards.push({
      label: 'Fields modified',
      value: uniqueFields,
      subtext: `${uniqueFields} different field${uniqueFields === 1 ? '' : 's'}`,
      color: '#00796B',
      bgColor: 'linear-gradient(135deg, #E0F2F1 0%, #B2DFDB 100%)',
    });
  }

  return cards;
}

export const ChangeHistoryInsightsSection: React.FC<{ rows: ChangeHistoryRow[] }> = ({ rows }) => {
  const insights = React.useMemo(() => computeInsights(rows), [rows]);
  if (insights.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: '#333' }}>
        Business Insights
      </Typography>
      <Grid container spacing={2}>
        {insights.map((card, idx) => (
          <Grid item xs={12} sm={6} md={4} key={idx}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                background: card.bgColor,
                borderLeft: `4px solid ${card.color}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                height: 100,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
              }}
            >
              <Typography variant="caption" sx={{ color: card.color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {card.label}
              </Typography>
              {typeof card.value === 'string' ? (
                <Tooltip title={card.value} arrow placement="top">
                  <Typography
                    variant="h6"
                    component="span"
                    sx={{
                      color: card.color,
                      fontWeight: 700,
                      mt: 0.5,
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {card.value}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography
                  variant="h6"
                  sx={{ color: card.color, fontWeight: 700, mt: 0.5 }}
                >
                  {card.value}
                </Typography>
              )}
              {card.subtext && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  {card.subtext}
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
